import { EducationalLevelFilterMapper } from "./mappers/EducationalLevelFilterMapper";
import { RouteLocation, Router, RouteLocationNormalized } from "vue-router";
import IExtendedSearchQueryFilter from "../interfaces/dtos/IExtendedSearchQueryFilter";
import { CountryFilterMapper } from "@app/routing/mappers/CountryFilterMapper";
import { DisciplineFilterMapper } from "@app/routing/mappers/DisciplineFilterMapper";
import { ContinentFilterMapper } from "@app/routing/mappers/ContinentFilterMapper";
import { AreaFilterMapper } from "@app/routing/mappers/AreaFilterMapper";
import { AttendanceFilterMapper } from "@app/routing/mappers/AttendanceFilterMapper";
import { DurationFilterMapper } from "@app/routing/mappers/DurationFilterMapper";
import { DegreeTypeFilterMapper } from "@app/routing/mappers/DegreeTypeFilterMapper";
import { EducationalFormFilterMapper } from "@app/routing/mappers/EducationalFormFilterMapper";
import { SpecialProgrammesFilterMapper } from "@app/routing/mappers/SpecialProgrammesFilterMapper";
import { TuitionFeeFilterMapper } from "@app/routing/mappers/TuitionFeeFilterMapper";
import { DensitiesFilterMapper } from "@app/routing/mappers/DensitiesFilterMapper";
import type { RouteMatchResult } from "@domain/interfaces/mappers/RouteMatchResult";
import { FilterType } from "@domain/enums/FilterType";
import { FilterMapper } from "@app/routing/mappers/FilterMapper";
import { OrganisationFilterMapper } from "@app/routing/mappers/OrganisationFilterMapper";

const filterTypes = Object.values(FilterType) as string[];
export class FilterProcessingService {
	private mappers: FilterMapper[] = [];
	private static instance: FilterProcessingService;

	public static getInstance(): FilterProcessingService {
		if (!FilterProcessingService.instance) {
			FilterProcessingService.instance = new FilterProcessingService();
		}
		return FilterProcessingService.instance;
	}

	private constructor() {
		this.mappers = [
			EducationalLevelFilterMapper.getInstance(),
			CountryFilterMapper.getInstance(),
			DisciplineFilterMapper.getInstance(),
			ContinentFilterMapper.getInstance(),
			AreaFilterMapper.getInstance(), //Problematic with region and country together
			AttendanceFilterMapper.getInstance(),
			DurationFilterMapper.getInstance(),
			DegreeTypeFilterMapper.getInstance(),
			EducationalFormFilterMapper.getInstance(),
			SpecialProgrammesFilterMapper.getInstance(),
			TuitionFeeFilterMapper.getInstance(),
			DensitiesFilterMapper.getInstance(),
			OrganisationFilterMapper.getInstance()
		]
	}

	public async processRoute(route: RouteLocation): Promise<IExtendedSearchQueryFilter[]> {
		const filters: IExtendedSearchQueryFilter[] = [];

		for (const mapper of this.mappers) {
			const values = await mapper.getExtendedFilterValuesFromRoute(route);

			if (values.length > 0) {
				filters.push({
					type: mapper.filterType,
					values
				})
			}
		}

		return filters;
	}

	public processFilters(router: Router, filters: IExtendedSearchQueryFilter[], routeLocation?: RouteLocationNormalized): RouteMatchResult {
		const params: Partial<Record<FilterType, string>> = {};
		const query: Partial<Record<string, string>> = {};

		const allRoutes = router.getRoutes();
		const eligibleFilterTypesForPath = filters
			.filter(filter => filter.values.length === 1 && filter.values[0]!.virtualPath)
			.map(filter => filter.type);
		let selectedRoute = allRoutes.find(route => route.name === 'Portal');
		for(const route of allRoutes) {
			if(!route.meta.filterTypes) { continue; }
			const selectedRouteFilterTypes = selectedRoute?.meta.filterTypes;
			if(selectedRouteFilterTypes && route.meta.filterTypes.length <= selectedRouteFilterTypes.length) continue;
			if(route.meta.filterTypes.length > eligibleFilterTypesForPath.length) continue;

			const includesAllFilters = route.meta.filterTypes.every(type => eligibleFilterTypesForPath.includes(type));
			if(includesAllFilters) selectedRoute = route;
		}
		
		const to: RouteLocationNormalized = routeLocation ?? routeLocation?.currentRoute.value;
		const nonFilterQueryKeys = Object.keys(to?.query || {}).filter(q => !filterTypes.includes(q));
		for (const key of nonFilterQueryKeys) {
			query[key] = to.query[key] as string;
		}


		const routeFilterTypes = selectedRoute?.meta.filterTypes ?? [];
		for(const filter of filters) {

			if(routeFilterTypes.includes(filter.type)) {
				params[filter.type] = filter.values[0]!.virtualPath;
				continue;
			}

			query[filter.type] = filter.values.map(v => v.id).join(',');
		}

		return {
			name: selectedRoute!.name!.toString(),
			params: params as Record<string, string>,
			query: query as Record<string, string>,
			hash: router.currentRoute.value.hash
		};
	}
}
